import { useState, useEffect, useRef, useCallback } from "react";
import "./LightBox.css";

export default function Lightbox({ image, onClose, maxZoom = 6 }) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const panStart = useRef(null);
  const stageRef = useRef(null);

  const close = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    onClose();
  }, [onClose]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = -e.deltaY;
    setZoom(z => {
      const next = +(Math.min(maxZoom, Math.max(1, z + delta * 0.0015))).toFixed(2);
      if (next === 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, [maxZoom]);

  useEffect(() => {
    if (!image) return;
    const node = stageRef.current;
    if (node) {
      node.addEventListener("wheel", handleWheel, { passive: false });
    }
    return () => {
      if (node) node.removeEventListener("wheel", handleWheel);
    };
  }, [image, handleWheel]);

  useEffect(() => {
    if (!image) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [image, close]);

  if (!image) return null;

  return (
    <div
      className="image-lightbox-overlay"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={image.alt || "Expanded image"}
      tabIndex={0}
    >
      <div className="image-lightbox-content" onClick={e => e.stopPropagation()}>
        <div
          ref={stageRef}
          className="image-lightbox-stage"
          onMouseDown={(e) => {
            if (zoom === 1) return;
            setPanning(true);
            panStart.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
          }}
          onMouseMove={(e) => {
            if (!panning) return;
            setOffset({
              x: e.clientX - panStart.current.x,
              y: e.clientY - panStart.current.y
            });
          }}
          onMouseUp={() => setPanning(false)}
          onMouseLeave={() => setPanning(false)}
          onDoubleClick={() => {
            setZoom(z => {
              const next = z === 1 ? 2 : 1;
              if (next === 1) setOffset({ x: 0, y: 0 });
              return next;
            });
          }}
        >
          <img
            src={image.src}
            alt={image.alt}
            draggable="false"
            className={`lightbox-img ${zoom > 1 ? "is-zoomed" : ""}`}
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              cursor: zoom > 1 ? (panning ? "grabbing" : "grab") : "zoom-in"
            }}
          />
        </div>
      </div>
    </div>
  );
}